/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { JsonReply, NextLocator, Partaker } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, throwError } from 'rxjs';
import { OU_URI } from '../ou/ou.types';
import { SERVICE_DTYPE_URI, ServicesService } from '../services/services.service';
import { NextServicio } from '../services/services.types';
import { BrandReply, BrandsReply, BusBrand, BusCustomer, CustomerReply, CustomersReply, DeleteNextServicioMissionInfoReply, FlushBrandReply, FlushCalendarReply, FlushCalendarRequest, FlushCustomerReply, FlushNextServicioMissionInfoBody, FlushNextServicioMissionInfoReply, FlushProjectReply, MissionInfo, NextBrand, NextCustomer, NextProject, ProjectReply, ProjectsReply, retrieveCalendarDateListReply, RetrieveCalendarDateListRequest, RetrieveCalendarWithDNReply, RetrieveCalendarWithDNRequest, RetrieveNextServicioMissionInfoBody, RetrieveNextServicioMissionInfoReply, RetrieveVisibilityMapReply, UserNodePrivileges, UserProfile, UserProfilePayload } from './projects.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { HttpService } from '../../../core/http/http.service';
import { LocalizePipe } from '../../../core/pipes/localize.pipe';
import { UserService } from '../../../core/user/user.service';
import { NextMethodsEnum } from '../../../core/enums/next-methods.enum';
import { FlushUserNodePriviligesRequest } from '../../../core/user/companyUser.model';

export const USER_URI = 'https://xml.cloudcodice.com/feature/company/User/1.1.0';
export const DTYPE_BRAND_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextmarca.NextMarca';
export const DTYPE_CUSTOMER_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextcliente.NextCliente';
export const DTYPE_PROJECT_URI = 'dtype://com.cloudcodice.thirdparty.grupoconstant.nextproyecto.NextProyecto';

@Injectable({
    providedIn: 'root'
})
export class ProjectsService {


    // BRANDS
    public readonly adminBrandProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileMasterMarca';

    // CUSTOMER
    public readonly adminCustomerProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileMasterClienteProyecto';
    public readonly backofficeCustomerProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileBackoffice';
    // PROJETS
    public readonly adminProjectProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileMasterClienteProyecto';
    public readonly backofficeProjectProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileBackoffice';
    public readonly viewerProjectProfileUri = 'userprofile://com.cloudcodice.thirdparty.grupoconstant.privileges.profile.UserProfileViewer';

    private _brands: BehaviorSubject<NextBrand[]> = new BehaviorSubject<NextBrand[]>([]);
    private _brand: BehaviorSubject<NextBrand> = new BehaviorSubject<NextBrand>(null);
    private _customers: BehaviorSubject<NextCustomer[]> = new BehaviorSubject<NextCustomer[]>([]);
    private _customer: BehaviorSubject<NextCustomer> = new BehaviorSubject<NextCustomer>(null);
    private _projects: BehaviorSubject<NextProject[]> = new BehaviorSubject<NextProject[]>([]);
    private _project: BehaviorSubject<NextProject> = new BehaviorSubject<NextProject>(null);

    /**
     * Constructor
     */
    constructor(
        private dialogsService: DialogsService,
        private httpService: HttpService,
        private localize: LocalizePipe,
        private servicesService: ServicesService,
        private transloco: TranslocoService,
        private router: Router,
        private userService: UserService
    ) {

    }

    get brands$(): Observable<NextBrand[]> {
        return this._brands.asObservable();
    }

    get brand$(): Observable<NextBrand> {
        return this._brand.asObservable();
    }

    get customers$(): Observable<NextCustomer[]> {
        return this._customers.asObservable();
    }

    get customer$(): Observable<NextCustomer> {
        return this._customer.asObservable();
    }

    get projects$(): Observable<NextProject[]> {
        return this._projects.asObservable();
    }

    get project$(): Observable<NextProject> {
        return this._project.asObservable();
    }

    getProfileNameByUri(uri: string) {
        if (uri.includes('UserProfileDeveloper')) {
            return 'developer';
        } else if (uri.includes('UserProfileSuperuser')) {
            return 'superuser';
        } else if (uri.includes('UserProfileMaster')) {
            return 'admin';
        } else if (uri.includes('UserProfileBackoffice')) {
            return 'backoffice';
        } else if (uri.includes('UserProfileViewer')) {
            return 'viewer';
        }
    }

    buildLocatorFromParams(params: Params): [NextLocator, string] {
        const locator: NextLocator = {
            OU: params?.ou,
            marca_code: params?.marca_code,
            cliente_code: params?.cliente_code,
            projectId: params?.projectId,
            serviceId: params?.serviceId
        };

        let stringLocator = '';
        if (params?.serviceId) {
            stringLocator += `Service=${params.serviceId},`;
        }
        if (params?.projectId) {
            stringLocator += `Project=${params.projectId},`;
        }
        if (params?.cliente_code) {
            stringLocator += `Customer=${params.cliente_code},`;
        }
        if (params?.marca_code) {
            stringLocator += `Brand=${params.marca_code},`;
        }
        if (params?.ou) {
            stringLocator += `OU=${params.ou}`;
        }

        return [locator, stringLocator];
    }

    getBelongsToCaption(locator: string, partaker: Partaker): string {
        const split = locator.split(',');
        const firstLocatorSplit = split[0].split('=');
        let secondLocatorSplit = [];
        if (split.length > 1) {
            secondLocatorSplit = split[1].split('='); // Only used for customers, it is the brand code
        }
        switch (firstLocatorSplit[0]) {
            case 'OU':
                return partaker[OU_URI][firstLocatorSplit[1]]?.name;
            case 'Brand':
                return `${partaker[DTYPE_BRAND_URI][firstLocatorSplit[1]]?.marca_name} (${this.transloco.translate('brand')})`;
            case 'Customer':
                return `${partaker[DTYPE_CUSTOMER_URI][`${secondLocatorSplit[1]}+${firstLocatorSplit[1]}`]?.name} (${this.transloco.translate('customer')})`;
            case 'Project':
                return `${partaker[DTYPE_PROJECT_URI][firstLocatorSplit[1]]?.name} (${this.transloco.translate('project')})`;
            case 'Service':
                return `${partaker[SERVICE_DTYPE_URI][firstLocatorSplit[1]]?.name} (${this.transloco.translate('service')})`;
            default:
                return '';
        }
    }

    isTheSameLocator(locator: string, objectLocator: string) {
        const split = locator.split(',');
        const dtypeSplit = objectLocator.split(',');
        return dtypeSplit[0] === split[0];
    }

    async getRoleByLocators(params: Params): Promise<UserNodePrivileges> {
        if (params.serviceId) {
            const service = await firstValueFrom(this.servicesService.service$);
            return { ...service.role };
        } else if (params.projectId) {
            let project = this._project.getValue();
            if (project?.projectId !== params.projectId) {
                project = await firstValueFrom(this.getProject(params.projectId));
            }
            return { ...project?.role };
        } else if (params.cliente_code && params.marca_code) {
            let customer = this._customer.getValue();
            if (customer?.cliente_code !== params.cliente_code || customer?.marca_code !== params.marca_code) {
                customer = await firstValueFrom(this.getCustomer(params.marca_code, params.cliente_code))
            }
            return { ...customer?.role };
        } else if (params.marca_code) {
            let brand = this._brand.getValue();
            if (brand?.marca_code !== params.marca_code) {
                brand = await firstValueFrom(this.getBrand(params.marca_code));
            }
            return { ...brand?.role };
        } else {
            const role = await firstValueFrom(this.userService.getRole());
            return role;
        }
    }

    async getRoleByNextLocators(locator: NextLocator): Promise<UserNodePrivileges> {
        if (locator.serviceId) {
            const service = await firstValueFrom(this.servicesService.service$);
            return { ...service.role };
        } else if (locator.projectId) {
            let project = this._project.getValue();
            if (project?.projectId !== locator.projectId) {
                project = await firstValueFrom(this.getProject(locator.projectId));
            }
            return { ...project?.role };
        } else if (locator.cliente_code && locator.marca_code) {
            let customer = this._customer.getValue();
            if (customer?.cliente_code !== locator.cliente_code || customer?.marca_code !== locator.marca_code) {
                customer = await firstValueFrom(this.getCustomer(locator.marca_code, locator.cliente_code))
            }
            return { ...customer?.role };
        } else if (locator.marca_code) {
            let brand = this._brand.getValue();
            if (brand?.marca_code !== locator.marca_code) {
                brand = await firstValueFrom(this.getBrand(locator.marca_code));
            }
            return { ...brand?.role };
        } else {
            const role = await firstValueFrom(this.userService.getRole());
            return role;
        }
    }

    hasPermissionToDesignRepository(role: UserNodePrivileges, facade: string /* 'master' | 'form' | 'tasktype' */) {
        switch (facade) {
            case 'master':
                return role?.masterDataDesigner;
            case 'form':
                return role?.formDesigner;
            case 'tasktype':
                return role?.taskTypeDesigner;
        }
    }

    retrieveNextServicioMissionInfo(body: RetrieveNextServicioMissionInfoBody): Observable<Partial<MissionInfo>> {
        return this.httpService.post<RetrieveNextServicioMissionInfoReply>(NextMethodsEnum.retrieveNextServicioMissionInfo, body).pipe(
            map(reply => reply.missionInfo ?? {})
        );

    }

    flushNextServicioMissionInfo(body: FlushNextServicioMissionInfoBody) {
        return this.httpService.post<FlushNextServicioMissionInfoReply>(NextMethodsEnum.flushNextServicioMissionInfo, body).pipe(
            map(reply => reply?.missionInfo ?? {})
        );
    }

    deleteNextServicioMissionInfo(body: RetrieveNextServicioMissionInfoBody) {
        return this.httpService.post<DeleteNextServicioMissionInfoReply>(NextMethodsEnum.deleteNextServicioMissionInfo, body);
    }

    getBrands(): Observable<NextBrand[]> {
        return this.httpService.post<BrandsReply>(NextMethodsEnum.retrieveNextMarcaList, {}).pipe(
            map((response) => {
                if (response && response.nextMarca) {
                    for (const brand of response.nextMarca) {
                        this.createProfileFields(brand, response.partaker);
                    }
                    this._brands.next(response.nextMarca.sort((a, b) => a.name.localeCompare(b.name)));
                    return response.nextMarca;
                } else {
                    this._brands.next([]);
                    return [];
                }
            })
        );
    }

    getBrand(marca_code: string, showForbiddenDialog = true): Observable<NextBrand> {
        return this.httpService.post<BrandReply>(NextMethodsEnum.retrieveNextMarca, {
            marca_code
        }).pipe(
            catchError((error) => {
                if (showForbiddenDialog && error.status === 403) {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('forbiddenBrand')
                    });
                } else if (error?.status === 404) {
                    this.dialogsService.openErrorDialog({
                        message: 'No se ha encontrado la marca',
                    });
                    this.router.navigate(['/404']);
                }
                return throwError(() => error);
            }),
            map((response) => {
                if (response && response.nextMarca) {
                    this.createProfileFields(response.nextMarca, response.partaker);
                    this._brand.next(response.nextMarca);
                    return response.nextMarca;
                } else {
                    this._brand.next(null);
                    return undefined;
                }
            })
        );
    }

    /**
     * Search brands from minibus
     *
     * @param expr
     */
    searchBusBrand(expr: string): Observable<BusBrand[]> {
        return this.httpService.post(NextMethodsEnum.searchBusMarca, {
            expr,
            excludeAssociatedMarca: false,
        }).pipe(
            map((response) => {
                if (response && response.busMarca) {
                    return response.busMarca;
                }
                return [];
            })
        );
    }

    async flushBrand(brand: NextBrand) {
        const body = { ...brand };
        if (body.admin) {
            body[this.adminBrandProfileUri] = body.admin.map(a => a.userId);
            delete body.admin;
        }

        delete body.busBrand;
        delete body.admin;

        try {
            await firstValueFrom(this.httpService.post<FlushBrandReply>(NextMethodsEnum.flushNextMarca, { nextMarca: body }).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog();
                    return throwError(() => error);
                })
            ));
        } catch (error) {
            console.error(error);
        }
    }

    async flushUserNodePriviliges(body: FlushUserNodePriviligesRequest): Promise<UserProfile> {
        return await firstValueFrom(this.userService.flushNodePrivileges(body).pipe(
            map((response) => {
                if (response?.userNodePrivileges?.length > 0) {
                    const users = response.userNodePrivileges.map((u) => {
                        const _user: UserProfile = { ...response.partaker[USER_URI][u.userId], ...u };
                        if (_user.grantor && _user.grantor !== 'system') {
                            _user.grantorName = response.partaker[USER_URI][_user.grantor].displayName;
                        }
                        return _user;
                    });
                    return users[0];
                }
            }),
        ));
    }

    async deleteBrand(marca_code: string) {
        try {
            const reply = await firstValueFrom(this.httpService.post(NextMethodsEnum.deleteNextMarca, { marca_code }));
            if (reply.deletedKey) {
                const brands = this._brands.getValue();
                brands.splice(brands.findIndex(b => b.marca_code === reply.deletedKey), 1);
                this._brands.next(brands);
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            this.dialogsService.openErrorDialog({
                message: error?.error?.echo ? this.localize.transform(error.error.echo) : undefined,
            });
            return false;
        }
    }

    getCustomers(marca_code: string): Observable<NextCustomer[]> {
        return this.httpService.post<CustomersReply>(NextMethodsEnum.retrieveNextClienteList, {
            marca_code,
            activeOnly: false
        }).pipe(
            map((response) => {
                if (response && response.nextCliente) {
                    for (const customer of response.nextCliente) {
                        this.createProfileFields(customer, response.partaker);
                        this.createBrandAndCustomerFields(customer, response.partaker);
                    }
                    this._customers.next(response.nextCliente.sort((a, b) => a.name.localeCompare(b.name)));
                    return response.nextCliente;
                } else {
                    this._customers.next([]);
                    return [];
                }
            })
        );
    }

    getCustomer(marca_code: string, cliente_code: number, showForbiddenDialog = true): Observable<NextCustomer> {
        return this.httpService.post<CustomerReply>(NextMethodsEnum.retrieveNextCliente, {
            marca_code,
            cliente_code
        }).pipe(
            catchError((error) => {
                if (showForbiddenDialog && error.status === 403) {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('forbiddenCustomer')
                    });
                } else if (error?.status === 404 || error?.status === 400) {
                    this.dialogsService.openErrorDialog({
                        message: 'No se ha encontrado el cliente',
                    });
                    this.router.navigate(['/404']);
                }
                return throwError(() => error);
            }),
            map((response) => {
                if (response && response.nextCliente) {
                    this.createProfileFields(response.nextCliente, response.partaker);
                    this.createBrandAndCustomerFields(response.nextCliente, response.partaker);
                    this._customer.next(response.nextCliente);
                    return response.nextCliente;
                } else {
                    this._customer.next(null);
                    return undefined;
                }
            })
        );
    }

    async flushCustomer(customer: NextCustomer) {
        const body = { ...customer };
        if (body.admin) {
            body[this.adminCustomerProfileUri] = body.admin.map(a => a.userId);
            delete body.admin;
        }
        if (body.backoffice) {
            body[this.backofficeCustomerProfileUri] = body.backoffice.map(d => d.userId);
            delete body.backoffice;
        }

        delete body.busCustomer;

        try {
            await firstValueFrom(this.httpService.post<FlushCustomerReply>(NextMethodsEnum.flushNextCliente, { nextCliente: body }).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog();
                    return throwError(() => error);
                })
            ));
        } catch (error) {
            console.error(error);
        }
    }

    async flushUser2Customer(body: any, userDatatypeUri: string, user: UserProfilePayload[]): Promise<UserProfile> {
        body.userDatatypeUri = userDatatypeUri;
        body.user = user;
        try {
            return await firstValueFrom(this.httpService.post(NextMethodsEnum.flushUser2NextCliente, body).pipe(
                map((response) => {
                    if (response && response.user2NextCliente && response.user2NextCliente.length > 0) {
                        const users = response.user2NextCliente.map((u) => {
                            const _user: UserProfile = { ...response.partaker[USER_URI][u.userId], ...u };
                            if (_user.grantor && _user.grantor !== 'system') {
                                _user.grantorName = response.partaker[USER_URI][_user.grantor].displayName;
                            }
                            return _user;
                        });
                        return users[0];
                    }
                }),
                catchError((error) => {
                    this.dialogsService.openErrorDialog();
                    return throwError(() => error);
                })
            ));
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Search customers from minibus
     *
     * @param expr
     */
    searchBusCustomer(expr: string, marca_code: string): Observable<BusCustomer[]> {
        return this.httpService.post(NextMethodsEnum.searchBusCliente, {
            expr,
            excludeAssociatedMarca: false,
            marca_code
        }).pipe(
            map((response) => {
                if (response && response.busCliente) {
                    return response.busCliente;
                }
                return [];
            })
        );
    }

    async deleteCustomer(marca_code: string, cliente_code: number) {
        try {
            const reply = await firstValueFrom(this.httpService.post(NextMethodsEnum.deleteNextCliente, { marca_code, cliente_code }));
            if (reply.deletedKey) {
                const customers = this._customers.getValue();
                customers.splice(customers.findIndex(c => c.marca_code === marca_code && c.cliente_code === cliente_code), 1);
                this._customers.next(customers);
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            this.dialogsService.openErrorDialog({
                message: error?.error?.echo ? this.localize.transform(error.error.echo) : undefined,
            });
            return false;
        }
    }

    getProjects(marca_code: string, cliente_code: number): Observable<NextProject[]> {
        return this.httpService.post<ProjectsReply>(NextMethodsEnum.retrieveNextProyectoList, {
            marca_code,
            cliente_code,
            activeOnly: false
        }).pipe(
            map((response) => {
                if (response && response.nextProyecto) {
                    for (const project of response.nextProyecto) {
                        this.createProfileFields(project, response.partaker);
                        this.createBrandAndCustomerFields(project, response.partaker);
                    }
                    this._projects.next(response.nextProyecto.sort((a, b) => a.name.localeCompare(b.name)));
                    return response.nextProyecto;
                } else {
                    this._projects.next([]);
                    return [];
                }
            })
        );
    }

    getProject(projectId: string, showForbiddenDialog = true): Observable<NextProject> {
        return this.httpService.post<ProjectReply>(NextMethodsEnum.retrieveNextProyecto, {
            projectId,
        }).pipe(
            catchError((error) => {
                if (showForbiddenDialog && error.status === 403) {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('forbiddenProject')
                    });
                }
                return throwError(() => error);
            }),
            map((response) => {
                if (response && response.nextProyecto) {
                    this.createProfileFields(response.nextProyecto, response.partaker);
                    this.createBrandAndCustomerFields(response.nextProyecto, response.partaker);
                    this._project.next(response.nextProyecto);
                    return response.nextProyecto;
                } else {
                    this._project.next(null);
                    return undefined;
                }
            })
        );
    }

    async flushProject(project: NextProject) {
        const projectBody = { ...project };

        if (projectBody.admin) {
            projectBody[this.adminProjectProfileUri] = projectBody.admin.map(a => a.userId);
            delete projectBody.admin;
        }
        if (projectBody.backoffice) {
            projectBody[this.backofficeProjectProfileUri] = projectBody.backoffice.map(d => d.userId);
            delete projectBody.backoffice;
        }
        if (projectBody.viewer) {
            projectBody[this.viewerProjectProfileUri] = projectBody.viewer.map(p => p.userId);
            delete projectBody.viewer;
        }

        try {
            await firstValueFrom(this.httpService.post<FlushProjectReply>(NextMethodsEnum.flushNextProyecto, { nextProyecto: projectBody }).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog();
                    return throwError(() => error);
                })
            ));
        } catch (error) {
            console.error(error);
        }
    }

    async flushUser2Project(body: any, userDatatypeUri: string, user: UserProfilePayload[]): Promise<UserProfile> {
        body.userDatatypeUri = userDatatypeUri;
        body.user = user;
        try {
            return await firstValueFrom(this.httpService.post(NextMethodsEnum.flushUser2NextProyecto, body).pipe(
                map((response) => {
                    if (response && response.user2NextProyecto && response.user2NextProyecto.length > 0) {
                        const users = response.user2NextProyecto.map((u) => {
                            const _user: UserProfile = { ...response.partaker[USER_URI][u.userId], ...u };
                            if (_user.grantor && _user.grantor !== 'system') {
                                _user.grantorName = response.partaker[USER_URI][_user.grantor].displayName;
                            }
                            return _user;
                        });
                        return users[0];
                    }
                }),
                catchError((error) => {
                    this.dialogsService.openErrorDialog();
                    return throwError(() => error);
                })
            ));
        } catch (error) {
            console.error(error);
        }
    }

    async deleteProject(projectId: string) {
        try {
            const reply = await firstValueFrom(this.httpService.post(NextMethodsEnum.deleteNextProyecto, { projectId }));
            if (reply.deletedKey) {
                const projects = this._projects.getValue();
                projects.splice(projects.findIndex(p => p.projectId === reply.deletedKey), 1);
                this._projects.next(projects);
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            this.dialogsService.openErrorDialog({
                message: error?.error?.echo ? this.localize.transform(error.error.echo) : undefined,
            });
            return false;
        }
    }

    async retrieveVisibilityMap() {
        const map = new Map<string, (NextBrand | NextCustomer | NextProject | NextServicio)[]>();
        const reply = await firstValueFrom(this.httpService.post<RetrieveVisibilityMapReply>(NextMethodsEnum.retrieveVisibilityMap, {}));
        for (const [uri, nodes] of Object.entries(reply.visibilityMap)) {
            for (const [key, node] of Object.entries(nodes)) {
                if (!map.has(uri)) {
                    map.set(uri, []);
                }
                map.get(uri).push({ ...reply.partaker[uri][key], byPass: node });
            }
        }
        return map;
    }

    retrieveCalendarWithDN(request: RetrieveCalendarWithDNRequest, stringLocator: string) {
        return this.httpService.post<RetrieveCalendarWithDNReply>(NextMethodsEnum.retrieveCalendarWithDN, request)
            .pipe(map(reply => {
                const calendar = reply?.collection?.map(cal => {
                    cal.belongsToCaption = this.getBelongsToCaption(cal.belongsTo, reply.partaker);
                    cal.sameLocator = this.isTheSameLocator(stringLocator, cal.belongsTo);
                    return cal;
                });
                return calendar ?? [];
            }));
    }

    retrieveCalendarDateList(request: RetrieveCalendarDateListRequest) {
        return this.httpService.post<retrieveCalendarDateListReply>(NextMethodsEnum.retrieveCalendarDateList, request)
            .pipe(map(reply => reply?.dates?.map(d => d.holiday) ?? []));
    }

    flushCalendar(request: FlushCalendarRequest) {
        return this.httpService.post<FlushCalendarReply>(NextMethodsEnum.flushCalendar, request)
        .pipe(map(reply => {
            const calendar = reply?.calendar;
            if (calendar) {
                calendar.belongsToCaption = this.getBelongsToCaption(calendar.belongsTo, reply.partaker);
                calendar.sameLocator = true;
            }
            return calendar;
        }));
    }

    deleteCalendar(calendarOid: string) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.deleteCalendar, { calendarOid });
    }

    private createProfileFields(object: any, partaker: Partaker) {
        for (const key of Object.keys(object).filter(k => k.startsWith('userprofile'))) {
            object[this.getProfileNameByUri(key)] = object[key].map((u) => {
                if (typeof u === 'string') {
                    const user: UserProfile = { ...partaker[USER_URI][u] };
                    user.userId = u;
                    return user;
                } else {
                    const user: UserProfile = { ...partaker[USER_URI][u.userId], ...u };
                    if (user.grantor && user.grantor !== 'system') {
                        user.grantorName = partaker[USER_URI][user.grantor].displayName;
                    }
                    return user;
                }
            });
        }
    }

    private createBrandAndCustomerFields(object: any, partaker: Partaker) {
        if (partaker && partaker[DTYPE_BRAND_URI]) {
            const brandPartakers = partaker[DTYPE_BRAND_URI];
            object.brand = brandPartakers[object.marca_code] as NextBrand;
            if (object.brand?.logo?.startsWith('http')) {
                object.brand.logo = this.httpService.getBaseUrl() + object.brand.logo;
            }
        }

        if (partaker && partaker[DTYPE_CUSTOMER_URI]) {
            const clientPartakers = partaker[DTYPE_CUSTOMER_URI];
            object.customer = clientPartakers[`${object.marca_code}+${object.cliente_code}`] as NextCustomer;
            if (object.customer?.logo?.startsWith('http')) {
                object.customer.logo = this.httpService.getBaseUrl() + object.customer.logo;
            }
        }
    }
}
